import { useMemo } from "react";
import type {
  CustomerBalance,
  CustomerSummary,
  Item,
  Vendor,
} from "../api/types";
import type { PickerPerson } from "../components/PersonPicker";
import { formatMoney } from "../lib/format";
import { useApi } from "./useApi";
import { useAuth } from "../auth/AuthContext";

/**
 * The picker lists are the same on every screen and change only when somebody
 * is added or a balance moves - and a write already calls reload(), which
 * refreshes them. Kept in memory for the sitting; see useApi.
 */
const CACHED = { cache: true };

export interface VendorBalanceRow {
  vendorId: string;
  name: string;
  qism?: string;
  commissionPct: string;
  /** What his deliveries have come to, before anything is taken off. */
  totalGoods: string;
  /** Money he has taken ahead and not yet had clawed back. */
  totalAdvance: string;
  net: string;
}

export interface PickerData<Row = unknown> {
  people: PickerPerson[];
  recentIds: string[];
  /** The rows behind the list, for screens that need more than a name. */
  rows: Row[];
  /**
   * True while the names are still on their way.
   *
   * <p>Without this the picker cannot tell "nobody is called that" from "the
   * list has not arrived yet" - both are an empty array - so it said «کوئی نام
   * نہیں ملا» at a customer who is in the book, and the search looked broken
   * when it was simply early. The longer the book gets, the longer that window
   * is, which is why it started being noticed now.
   */
  loading: boolean;
  reload: () => void;
}

/**
 * Customer names for the picker - carrying each balance for the owner, and the
 * names alone for the counter.
 *
 * Shared by every screen that picks a customer, so the list looks and reads the
 * same on all of them, and so the rule about who sees a balance is written once.
 */
export function useCustomerPicker(): PickerData<CustomerBalance> {
  const { isOwner } = useAuth();

  /*
   * Two lists, and which one is fetched matters.
   *
   * The owner's list carries every balance, which is why it is worth the extra
   * request: seeing that somebody already owes 23,950 before writing them
   * another credit sale is exactly the moment that figure is useful.
   *
   * The counter is not shown balances, so it must not ASK for them either. The
   * server refuses that route to a STAFF account, and a hook that requested it
   * anyway would get a 403 and render an empty picker - no names, no way to
   * write a credit sale, and nothing on screen saying why. Asking for the plain
   * name list instead is not a workaround; it is the same rule, obeyed on both
   * sides.
   */
  const balances = useApi<CustomerBalance[]>(
    isOwner ? "/customers/balances?includeSettled=true" : null,
    [],
    CACHED,
  );
  const names = useApi<CustomerSummary[]>(
    isOwner ? null : "/customers",
    [],
    CACHED,
  );
  const recent = useApi<string[]>("/customers/recent", [], CACHED);

  const people = useMemo<PickerPerson[]>(() => {
    if (isOwner) {
      return (balances.data ?? []).map((row) => ({
        id: row.customer.id,
        name: row.customer.name,
        note: formatMoney(row.totals.balance),
        noteTone: Number(row.totals.balance) > 0 ? "due" : "clear",
      }));
    }
    return (names.data ?? []).map((row) => ({ id: row.id, name: row.name }));
  }, [isOwner, balances.data, names.data]);

  return {
    people,
    recentIds: recent.data ?? [],
    rows: balances.data ?? [],
    loading: balances.loading || names.loading,
    reload: () => {
      balances.reload();
      names.reload();
      recent.reload();
    },
  };
}

/**
 * Grower names for the picker, each carrying what is still owed to them.
 *
 * Left uncoloured on purpose. On the customer side red means somebody owes the
 * shop, which is a risk worth flagging; owing a زمیندار is just how the trade
 * works, and painting it red every day would train the eye to ignore the colour
 * where it does matter.
 */
export function useVendorPicker(): PickerData<VendorBalanceRow> {
  const { isOwner } = useAuth();

  // Same rule as the customers: what a grower is owed is the owner's figure, so
  // the counter asks for the names only. It still needs them - a delivery has to
  // be written against somebody.
  const balances = useApi<VendorBalanceRow[]>(
    isOwner ? "/vendors/balances" : null,
    [],
    CACHED,
  );
  const names = useApi<Vendor[]>(isOwner ? null : "/vendors", [], CACHED);
  const recent = useApi<string[]>("/vendors/recent", [], CACHED);

  const people = useMemo<PickerPerson[]>(() => {
    if (isOwner) {
      return (balances.data ?? []).map((row) => ({
        id: row.vendorId,
        name: row.name,
        note: formatMoney(row.net),
      }));
    }
    return (names.data ?? []).map((row) => ({ id: row.id, name: row.name }));
  }, [isOwner, balances.data, names.data]);

  return {
    people,
    recentIds: recent.data ?? [],
    rows: balances.data ?? [],
    loading: balances.loading || names.loading,
    reload: () => {
      balances.reload();
      names.reload();
      recent.reload();
    },
  };
}

/** Flowers for a picker. The note is the usual rate, which is what you want to see. */
export function useItemPicker(): PickerData<Item> {
  const items = useApi<Item[]>("/items", [], CACHED);
  // The shop sells the same two or three flowers all week, so the ones used most
  // recently lead the list - and the sale form opens on the first of them.
  const recent = useApi<string[]>("/items/recent?limit=8", [], CACHED);

  const people = useMemo<PickerPerson[]>(
    () =>
      (items.data ?? []).map((item) => ({
        id: item.id,
        name: item.name,
        note: formatMoney(item.defaultRate),
      })),
    [items.data],
  );

  return {
    people,
    recentIds: recent.data ?? [],
    rows: items.data ?? [],
    loading: items.loading,
    reload: () => {
      items.reload();
      recent.reload();
    },
  };
}
